import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogData, AbstractDialog, AbstractDialogResultData } from '@next/core-lib/dialog';

import { Store } from '@ngrx/store';
import { Observable, concatMap, groupBy, map, mergeMap, of, toArray, zip } from 'rxjs';
import { PreferredDeliveryPeriods } from 'src/app/overview/models';
import { SiteState } from 'src/app/shared/models';

import { handleConfirmOrderForOverview } from '../../../store/actions';
import { CropAid, DigitalProductSubscription, Package, PeriodOrderPlanWithDetails } from '../../models';

export interface ConfirmOrderDialogDataOverviewV2 extends AbstractDialogData {
  orderplanByPeriods: PeriodOrderPlanWithDetails[];
  preferedDeliveryPeriods: PreferredDeliveryPeriods;
  expiredOrderplanByPeriods: PeriodOrderPlanWithDetails[];
  inactiveCropAids: CropAid[];
  inactivePackages: Package[];
  cropAids: CropAid[];
  digitalProductSubscriptions: DigitalProductSubscription[];
}

@Component({
  selector: 'app-confirm-order-dialog',
  templateUrl: './confirm-order-dialog.component.html',
  styleUrls: ['./confirm-order-dialog.component.scss'],
  standalone: false,
})
export class ConfirmOrderDialogOverviewV2Component extends AbstractDialog {
  private readonly store = inject<Store<SiteState>>(Store);
  data = inject<ConfirmOrderDialogDataOverviewV2>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef = inject<MatDialogRef<ConfirmOrderDialogOverviewV2Component, AbstractDialogResultData<never>>>(MatDialogRef);

  public isOrderValid: boolean = !this.data.inactiveCropAids.length && !this.data.inactivePackages.length && !this.data.expiredOrderplanByPeriods.length;

  groupedOrderPlansByPeriods$ = this.groupOrderplanByPeriod(this.data.orderplanByPeriods);

  getCropAid(id: number) {
    return this.data.cropAids.find((cropAid) => cropAid.id === id);
  }

  getCropAidCodeToDisplay(id: number) {
    const cropAidCode = this.data.cropAids.find((cropAid) => cropAid.id === id)?.codes[0];
    if (!cropAidCode) {
      return;
    }
    return `(${cropAidCode})`;
  }

  showWarningForPeriod(periodId: number) {
    return this.data.preferedDeliveryPeriods && this.data.preferedDeliveryPeriods.periods.indexOf(periodId) === -1;
  }

  getToOrderOfDigitalProductSubscription(digitalProduct: DigitalProductSubscription): number {
    return Math.ceil(digitalProduct.totalRequisite - digitalProduct.alreadyOrdered - digitalProduct.correction);
  }

  /*
    A mat-dialog adds a div with class 'cdk-global-overlay-wrapper' to a
    div with class 'cdk-overlay-container' at the bottom of the .html,
    so the dialogs get nested behind one another.
    The ConfirmOrder dialog gets automatically dismissed and makes
    SelectPeriodsToOrder dialog to be dismissed with it.

    This function overrides the code from the effects -> confirmOrder.

    This is a fix for the nested dialog that closes immediately after it's opened
  */
  confirm(): void {
    this.store.dispatch(handleConfirmOrderForOverview());
  }

  private groupOrderplanByPeriod(orderplans: PeriodOrderPlanWithDetails[]): Observable<{ periodId: number; orderLines: PeriodOrderPlanWithDetails[] }[]> {
    return of(orderplans).pipe(
      // Emit the array elements one by one for the groupBy
      concatMap((orderPlans) => orderPlans),
      groupBy((orderPlan) => orderPlan.periodId),
      // Merge each grouped set
      mergeMap((group) => zip(of(group.key), group.pipe(toArray())).pipe(map(([periodId, orderLines]) => ({ periodId, orderLines })))),
      toArray(),
    );
  }
}
